import { API_BASE_URL } from '../config';
import React, { useState } from 'react';
import { emptyVitals, sanitizeVitals } from '../utils/patientVitals';

const PatientRegistration = () => {
  const [patientData, setPatientData] = useState({
    PATIENT_NAME: '',
    PATIENT_AGE: '',
    PATIENT_GENDER: '',
    PATIENT_PHONE: '',
    PATIENT_ADDRESS: '',
    DATE_OF_BIRTH: '',
    EMAIL: '',
    EMERGENCY_CONTACT: '',
    EMERGENCY_CONTACT_PHONE: '',
    BLOOD_TYPE: '',
    MEDICAL_HISTORY: '',
    ALLERGIES: '',
    INSURANCE_PROVIDER: '',
    INSURANCE_ID: '',
    PHOTO_ID: '',
    VITALS: emptyVitals(),
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleVitalChange = (e) => {
    const { name, value } = e.target;
    setPatientData((prevData) => ({
      ...prevData,
      VITALS: { ...prevData.VITALS, [name]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const token = localStorage.getItem('token');

    // Client-side validation
    const requiredFields = [
      'PATIENT_NAME',
      'PATIENT_AGE',
      'PATIENT_GENDER',
      'PATIENT_PHONE',
      'PATIENT_ADDRESS',
      'DATE_OF_BIRTH',
      'EMAIL',
      'EMERGENCY_CONTACT',
      'EMERGENCY_CONTACT_PHONE',
    ];

    for (const field of requiredFields) {
      if (patientData[field] === '') {
        setError(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
        return;
      }
    }

    if (!/^\d+$/.test(patientData.PATIENT_AGE) || parseInt(patientData.PATIENT_AGE) <= 0) {
      setError('Please enter a valid age.');
      return;
    }

    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(patientData.EMAIL)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!/^\d{10}$/.test(patientData.PATIENT_PHONE)) {
      setError('Please enter a valid 10-digit patient phone number.');
      return;
    }

    if (!/^\d{10}$/.test(patientData.EMERGENCY_CONTACT_PHONE)) {
      setError('Please enter a valid 10-digit emergency contact phone number.');
      return;
    }

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    let vitals;
    try {
      vitals = sanitizeVitals(patientData.VITALS);
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    const dataToSubmit = { ...patientData, VITALS: vitals };
    // Handle optional fields: remove if empty to avoid sending empty strings for fields backend might expect as missing
    for (const key in dataToSubmit) {
      if (key === 'PATIENT_AGE' && typeof dataToSubmit[key] === 'string' && dataToSubmit[key] !== '') {
        dataToSubmit[key] = parseInt(dataToSubmit[key], 10); // Convert age to number
      }
      if (['BLOOD_TYPE', 'MEDICAL_HISTORY', 'ALLERGIES', 'INSURANCE_PROVIDER', 'INSURANCE_ID', 'PHOTO_ID'].includes(key) && dataToSubmit[key] === '') {
        delete dataToSubmit[key];
      }
    }
    if (!Object.keys(vitals).length) delete dataToSubmit.VITALS;

    try {
      const response = await fetch(`${API_BASE_URL}/patients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSubmit),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Patient registered successfully!');
        // Optionally clear the form after successful registration
        setPatientData({
          PATIENT_NAME: '',
          PATIENT_AGE: '',
          PATIENT_GENDER: '',
          PATIENT_PHONE: '',
          PATIENT_ADDRESS: '',
          DATE_OF_BIRTH: '',
          EMAIL: '',
          EMERGENCY_CONTACT: '',
          EMERGENCY_CONTACT_PHONE: '',
          BLOOD_TYPE: '',
          MEDICAL_HISTORY: '',
          ALLERGIES: '',
          INSURANCE_PROVIDER: '',
          INSURANCE_ID: '',
          PHOTO_ID: '',
          VITALS: emptyVitals(),
        });
      } else {
        setError(data.message || 'Failed to register patient.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '2rem auto', padding: '2rem', border: '1px solid #ccc', borderRadius: 8 }}>
      <h2>Register Patient</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label>Patient Name:</label>
          <input
            type="text"
            name="PATIENT_NAME"
            value={patientData.PATIENT_NAME}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Patient Age:</label>
          <input
            type="number"
            name="PATIENT_AGE"
            value={patientData.PATIENT_AGE}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Date of Birth:</label>
          <input
            type="date"
            name="DATE_OF_BIRTH"
            value={patientData.DATE_OF_BIRTH}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Patient Gender:</label>
          <select
            name="PATIENT_GENDER"
            value={patientData.PATIENT_GENDER}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Patient Address:</label>
          <input
            type="text"
            name="PATIENT_ADDRESS"
            value={patientData.PATIENT_ADDRESS}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Patient Phone:</label>
          <input
            type="tel"
            name="PATIENT_PHONE"
            value={patientData.PATIENT_PHONE}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Email:</label>
          <input
            type="email"
            name="EMAIL"
            value={patientData.EMAIL}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Emergency Contact:</label>
          <input
            type="text"
            name="EMERGENCY_CONTACT"
            value={patientData.EMERGENCY_CONTACT}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Emergency Contact Phone:</label>
          <input
            type="tel"
            name="EMERGENCY_CONTACT_PHONE"
            value={patientData.EMERGENCY_CONTACT_PHONE}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <h3 style={{ margin: '1.5rem 0 0.75rem' }}>Vitals (optional)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <label>Blood pressure (mmHg):</label>
            <input type="text" name="BLOOD_PRESSURE" placeholder="120/80" value={patientData.VITALS.BLOOD_PRESSURE} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
          <div>
            <label>Pulse (bpm):</label>
            <input type="number" name="PULSE" value={patientData.VITALS.PULSE} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
          <div>
            <label>Temperature (°F):</label>
            <input type="number" name="TEMPERATURE" step="0.1" value={patientData.VITALS.TEMPERATURE} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
          <div>
            <label>Respiratory rate (/min):</label>
            <input type="number" name="RESPIRATORY_RATE" value={patientData.VITALS.RESPIRATORY_RATE} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
          <div>
            <label>SpO2 (%):</label>
            <input type="number" name="SPO2" value={patientData.VITALS.SPO2} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
          <div>
            <label>Weight (kg):</label>
            <input type="number" name="WEIGHT" step="0.1" value={patientData.VITALS.WEIGHT} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
          <div>
            <label>Height (cm):</label>
            <input type="number" name="HEIGHT" step="0.1" value={patientData.VITALS.HEIGHT} onChange={handleVitalChange} style={{ width: '100%', padding: '0.5rem' }} />
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Blood Type (Optional):</label>
          <input
            type="text"
            name="BLOOD_TYPE"
            value={patientData.BLOOD_TYPE}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Medical History (Optional):</label>
          <input
            type="text"
            name="MEDICAL_HISTORY"
            value={patientData.MEDICAL_HISTORY}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Allergies (Optional):</label>
          <input
            type="text"
            name="ALLERGIES"
            value={patientData.ALLERGIES}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Insurance Provider (Optional):</label>
          <input
            type="text"
            name="INSURANCE_PROVIDER"
            value={patientData.INSURANCE_PROVIDER}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Insurance ID (Optional):</label>
          <input
            type="text"
            name="INSURANCE_ID"
            value={patientData.INSURANCE_ID}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label>Photo ID (Optional):</label>
          <input
            type="text"
            name="PHOTO_ID"
            value={patientData.PHOTO_ID}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>
        {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
        {success && <div style={{ color: 'green', marginBottom: '1rem' }}>{success}</div>}
        <button type="submit" style={{ padding: '0.5rem 1rem' }}>Register Patient</button>
      </form>
    </div>
  );
};

export default PatientRegistration;
